'use client';

import { UIEvent, useEffect, useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { CartActions } from './cart-actions.component';
import { CartCouponSection } from './cart-coupon/cart-coupon-section.component';
import { CartDrawerHeader } from './cart-drawer-header.component';
import { CartEmptyState } from './cart-empty-state.component';
import { CartFooter } from './cart-footer.component';
import { CartItemsList } from './cart-items-list.component';

import { Skeleton } from '@/components/ui/skeleton.component';
import { useToast } from '@/components/ui/use-toast';
import { useCart } from '@/hooks/use-cart.hook';
import { retryCart } from '@/store/cart/cart.store';

export const CART_LOAD_ERROR_MESSAGE = 'Erro ao carregar seu carrinho.';
export const CART_LOAD_RETRY_LABEL = 'Tentar novamente';

interface CartDrawerProps {
  onAddProcedure: () => void;
  onSearch: () => void;
  onProceed: () => void;
  isProceeding?: boolean;
}

export function CartDrawer({ onAddProcedure, onSearch, onProceed, isProceeding }: CartDrawerProps) {
  const { items, itemCount, total, couponDiscount, isLoading, hasError, isEmpty, hasItems } = useCart();
  const { toast } = useToast();
  const [isScrolled, setIsScrolled] = useState(false);

  function handleScroll(event: UIEvent<HTMLDivElement>) {
    setIsScrolled(event.currentTarget.scrollTop > 0);
  }

  useEffect(() => {
    if (!hasError) {
      return;
    }

    toast.error(CART_LOAD_ERROR_MESSAGE, {
      action: { text: CART_LOAD_RETRY_LABEL, onClick: () => void retryCart() },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  const showsEmptyState = isEmpty || hasError;
  const showsCouponSection = !isLoading && !hasError;

  return (
    <div className="relative flex h-full flex-col" data-testid={DataTestIdEnum.CART_DRAWER}>
      <div
        className={`relative z-10 shrink-0 bg-white transition-shadow ${
          isScrolled ? 'shadow-modal' : ''
        }`}
      >
        <CartDrawerHeader itemCount={itemCount} />

        {hasItems && (
          <div className="px-6 pb-2">
            <CartActions onAddProcedure={onAddProcedure} />
          </div>
        )}
      </div>

      <div
        className={`min-h-0 flex-1 overflow-y-auto ${hasItems ? 'pt-2 pb-28' : ''}`}
        onScroll={handleScroll}
      >
        {showsCouponSection && <CartCouponSection />}

        {isLoading && (
          <div className="flex flex-col gap-4 px-6 pb-4" data-testid={DataTestIdEnum.CART_LOADING}>
            <Skeleton className="h-40 w-full rounded-xxl" />
            <Skeleton className="h-40 w-full rounded-xxl" />
          </div>
        )}

        {showsEmptyState && <CartEmptyState onSearch={onSearch} />}

        {hasItems && <CartItemsList items={items} />}
      </div>

      {hasItems && (
        <CartFooter total={total} discount={couponDiscount} onProceed={onProceed} isProceeding={isProceeding} />
      )}
    </div>
  );
}
